import * as React from 'react';
import { useState, useEffect, useMemo } from 'react';
import { Select } from '@kmx/legos-react-select';
import { Checkbox } from '@kmx/legos-react-checkbox';
import { Button as KmxButton } from '@kmx/legos-react-button';
import ValidatedArea from '../../ValidatedArea';
import VehicleStyleSelector from './VehicleStyleSelector';
import { useVehicleInfo } from '../../../context/vehicleInfo';
import {
    AVAILABLE_DRIVES,
    AVAILABLE_FUELS,
    AVAILABLE_TRANSMISSIONS,
    findMatchingSku,
    skuGroupToUniqueStyles,
    toggleStyleOption,
} from '../../../utils/sku-helpers';
import useFormFeaturesState from '../../../hooks/useFormFeaturesState';
import { FORM_FIELD_NAMES } from '../../../constants';
import { IVehicleStyleDetail, IVehicleStyleItem } from '../../../types/IVehicleStyle';
import { withAnalyticsClickTracking } from '../../../utils/analytics';
const Button = withAnalyticsClickTracking(KmxButton);

import * as styles from './FeaturesBody.module.scss';

interface IFeaturesBodyProps {
    onCompleteChange?(isComplete: boolean): void; // Injected by the AccordionStepper
    allValidationTriggered: boolean;
    readonly: boolean;
}

const FeaturesBody: React.FC<IFeaturesBodyProps> = ({ onCompleteChange, allValidationTriggered, readonly }) => {
    const { featureInfo, setFeatureInfo, featureLookupInfo, setFeatureLookupInfo } = useVehicleInfo();
    const [standardFeaturesVisible, setStandardFeaturesVisible] = useState(false);
    const [style, setStyle] = useState(
        featureInfo.style
            ? featureLookupInfo.flat().find(y => y.sku?.toUpperCase() === featureInfo?.style?.id?.toUpperCase())
            : null
    );
    const [offerRefreshStyleGroupSet, setOfferRefreshStyleGroupSet] = useState(featureInfo.style ? true : false);
    const [styleGroup, setStyleGroup] = useState<IVehicleStyleDetail[] | null>(() => {
        if (!style) return null;
        const arr = [
            featureLookupInfo.flat().find(y => y.sku?.toUpperCase() === featureInfo?.style?.id?.toUpperCase()),
        ].filter((x): x is IVehicleStyleDetail => x != null);
        return arr.length > 0 ? arr : null;
    });
    const [drive, setDrive] = useFormFeaturesState(featureInfo.drive ?? '');
    const [transmission, setTransmission] = useFormFeaturesState(featureInfo.transmission ?? '');
    const [fuel, setFuel] = useFormFeaturesState(featureInfo.fuel ?? '');
    const [availableOptions, setAvailableOptions] = useState(featureInfo.style ? featureInfo.availableOptions : []);
    const [standardOptions, setStandardOptions] = useState(featureInfo.style ? featureInfo.standardOptions : []);

    const skuGroupStyles = useMemo(() => {
        if (!styleGroup) return null;
        return skuGroupToUniqueStyles(styleGroup);
    }, [styleGroup]);

    const isFuelRequired = useMemo(() => (skuGroupStyles?.fuels.length ?? 0) > 1, [skuGroupStyles]);

    const isComplete = useMemo(() => {
        return (
            styleGroup != null &&
            drive !== null &&
            drive != '' &&
            transmission !== null &&
            transmission != '' &&
            (!isFuelRequired || fuel != '')
        );
    }, [drive, fuel, isFuelRequired, styleGroup, transmission]);

    // When the available styles change, update step completion as appropriate
    useEffect(() => {
        onCompleteChange(isComplete);
    }, [isComplete, onCompleteChange]);

    useEffect(() => {
        if (style && drive && drive != '' && transmission && transmission != '' && (!isFuelRequired || fuel != '')) {
            setFeatureInfo(prev => ({
                ...prev,
                style: { id: style.sku, description: null },
            }));
        }

        // Save the selected trim & features.
        setFeatureInfo(prev => ({
            ...prev,
            isComplete: isComplete,
            isFuelRequired,
            drive,
            transmission,
            fuel,
            availableOptions,
            standardOptions,
        }));
    }, [
        style,
        drive,
        transmission,
        fuel,
        isFuelRequired,
        isComplete,
        setFeatureInfo,
        setFeatureLookupInfo,
        availableOptions,
        standardOptions,
    ]);

    const getFeatureCheckbox = (options: string[], setter: (a: string[]) => void, feature: IVehicleStyleItem) => {
        return (
            <Checkbox
                key={feature.code}
                id={'ico-cb-feature-' + feature.code}
                name={feature.code}
                checked={options.includes(feature.code)}
                onChange={() => setter(toggleStyleOption(options, feature.code))}
                label={feature.display}
                disabled={readonly}
            />
        );
    };

    useEffect(() => {
        if (styleGroup) {
            //don't update for the first time through during offer refresh
            if (!offerRefreshStyleGroupSet) {
                if (!skuGroupStyles) return;
                const { drives, transmissions, fuels } = skuGroupStyles;
                setDrive(drives.length === 1 ? drives[0] : '');
                setTransmission(transmissions.length === 1 ? transmissions[0] : '');
                setFuel(fuels.length === 1 ? fuels[0] : '');
            }
            //once changed, during offer refresh, then update normally
            // drive, transmission, and fuel are preserved via their state initializers (featureInfo.drive/transmission/fuel)
            else {
                setOfferRefreshStyleGroupSet(false);
            }
        }
    }, [styleGroup]);

    //all answers selected, find and select the correct style sku
    useEffect(() => {
        if (styleGroup && transmission != '' && drive != '' && (!isFuelRequired || fuel != '')) {
            const foundStyle = findMatchingSku(styleGroup, drive, transmission, fuel);

            setStyle(foundStyle);
            setStandardOptions(foundStyle.standardOptions.map(f => f.code));
        } else {
            setStyle(null);
            setAvailableOptions([]);
            setStandardOptions([]);
        }
    }, [fuel, isFuelRequired, styleGroup, transmission, drive]);

    useEffect(() => {
        if (style && (style.standardOptions.length === 0 || style.availableOptions.length === 0)) {
            setStandardFeaturesVisible(true);
        }
    }, [style]);

    return (
        <div id="icoFeaturesBody" className={styles.container}>
            <h3 className="kmx-typography--headline-2">Style</h3>
            <div className={styles.styleWrapper}>
                <ValidatedArea
                    overlayIcon={true}
                    invalid={allValidationTriggered && !styleGroup}
                    validationMessage="Please make a selection above"
                >
                    <VehicleStyleSelector
                        readOnly={readonly}
                        style={style}
                        styleGroup={styleGroup}
                        setStyleGroup={setStyleGroup}
                        offerRefreshStyleGroupSet={offerRefreshStyleGroupSet}
                    />
                </ValidatedArea>
            </div>
            <div className={styles.driveWrapper}>
                <ValidatedArea
                    overlayIcon={true}
                    invalid={allValidationTriggered && !drive}
                    validationMessage="Please make a selection above"
                >
                    <Select
                        label="Drive"
                        id="ico-features-drive"
                        name={FORM_FIELD_NAMES.vehicleFeatures.drive}
                        options={AVAILABLE_DRIVES}
                        onChange={e => setDrive(e.target.value)}
                        value={drive}
                        disabled={styleGroup == null || readonly}
                    />
                </ValidatedArea>
            </div>
            <div className={styles.transmissionWrapper}>
                <ValidatedArea
                    overlayIcon={true}
                    invalid={allValidationTriggered && !transmission}
                    validationMessage="Please make a selection above"
                >
                    <Select
                        label="Transmission"
                        id="ico-features-transmission"
                        name={FORM_FIELD_NAMES.vehicleFeatures.transmission}
                        options={AVAILABLE_TRANSMISSIONS}
                        onChange={e => setTransmission(e.target.value)}
                        value={transmission}
                        disabled={styleGroup == null || readonly}
                    />
                </ValidatedArea>
            </div>
            {isFuelRequired && (
                <div className={styles.fuelWrapper}>
                    <ValidatedArea
                        overlayIcon={true}
                        invalid={allValidationTriggered && !fuel}
                        validationMessage="Please make a selection above"
                    >
                        <Select
                            label="Fuel"
                            id="ico-features-fuel"
                            name={FORM_FIELD_NAMES.vehicleFeatures.fuel}
                            options={AVAILABLE_FUELS}
                            onChange={e => setFuel(e.target.value)}
                            value={fuel}
                            disabled={styleGroup == null || readonly}
                        />
                    </ValidatedArea>
                </div>
            )}
            {style && transmission != '' && (style.availableOptions.length > 0 || style.standardOptions.length > 0) && (
                <div id="icoFeaturesBody-Features" className={styles.features}>
                    <h3 className="kmx-typography--headline-2">Features</h3>
                    <fieldset className="kmx-flex-wrapper">
                        {style.availableOptions
                            .filter(option => !style.standardOptions.some(val => val.code === option.code))
                            .map((f: IVehicleStyleItem) =>
                                getFeatureCheckbox(availableOptions, setAvailableOptions, f)
                            )}
                        {standardFeaturesVisible &&
                            style.standardOptions.map((f: IVehicleStyleItem) =>
                                getFeatureCheckbox(standardOptions, setStandardOptions, f)
                            )}
                    </fieldset>
                    {style.standardOptions.length > 0 && (
                        <Button
                            id={standardFeaturesVisible ? 'ico-features-hideStandard' : 'ico-features-showStandard'}
                            level="tertiary"
                            className={styles.showStandardFeaturesButton}
                            onClick={() => setStandardFeaturesVisible(!standardFeaturesVisible)}
                        >
                            {`${standardFeaturesVisible ? 'Hide' : 'Show'} standard features`}
                        </Button>
                    )}
                </div>
            )}
        </div>
    );
};

export default FeaturesBody;
